import React, { useState, useEffect } from "react";
import { PostCreateAdmin } from "./apis/post";
import { GetAllAdmins } from "./apis/get";
import { useAuth } from "./features/AuthContext";
import { useNavigate } from "react-router-dom";

interface Admin {
  admin_id: string;
  username: string;
  email: string;
  phone: number | null;
  role: string;
  is_active: boolean;
  created_at: string;
  last_login_at: string | null;
}

const AVAILABLE_ROLES = [
  "SUPER_ADMIN",
  "ADMIN",
  "MODERATOR",
  "SUPPORT",
  "MARKETING",
  "ACCOUNTANT",
  "HR",
  "CUSTOMER_SUPPORT",
  "CALLING_TEAM_LEAD",
];

const CREATE_ADMIN_ROLES = AVAILABLE_ROLES.filter((role) => role !== "SUPER_ADMIN");

export default function AdminManagement() {
  const { role } = useAuth();
  const navigate = useNavigate();

  const [admins, setAdmins] = useState<Admin[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [adminCount, setAdminCount] = useState(0);
  const [adminTotal, setAdminTotal] = useState(0);
  const [listFilters, setListFilters] = useState({
    search: "",
    role: "",
    status: "",
  });

  // Form state
  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    phone: "",
    role: "ADMIN",
  });

  // Check if user is SUPER_ADMIN
  useEffect(() => {
    if (role !== "SUPER_ADMIN") {
      navigate("/dashboard");
    }
  }, [role, navigate]);

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async (filters = listFilters) => {
    setLoading(true);
    try {
      const response = await GetAllAdmins(filters);
      if (response.status === "success") {
        const data = Array.isArray(response.data) ? response.data : [];
        setAdmins(data);
        setAdminCount(response.count ?? 0);
        setAdminTotal(response.total ?? 0);
      } else {
        alert(response.detail || "Failed to fetch admins");
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
      alert("Failed to fetch admins");
    } finally {
      setLoading(false);
    }
  };

  const handleListFilterChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setListFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyListFilters = () => {
    fetchAdmins(listFilters);
  };

  const clearListFilters = () => {
    const nextFilters = { search: "", role: "", status: "" };
    setListFilters(nextFilters);
    fetchAdmins(nextFilters);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCreateAdmin = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validation
    if (formData.username.length < 3) {
      alert("Username must be at least 3 characters");
      return;
    }

    if (formData.password.length < 6) {
      alert("Password must be at least 6 characters");
      return;
    }

    if (!formData.email.includes("@")) {
      alert("Please enter a valid email");
      return;
    }

    if (formData.phone && formData.phone.length !== 10) {
      alert("Phone must be 10 digits");
      return;
    }

    setCreating(true);
    try {
      const payload: any = {
        username: formData.username,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };

      if (formData.phone) {
        payload.phone = parseInt(formData.phone);
      }

      const response = await PostCreateAdmin(payload);

      if (response.status === "success") {
        alert("Admin created successfully!");
        // Reset form
        setFormData({
          username: "",
          email: "",
          password: "",
          phone: "",
          role: "ADMIN",
        });
        // Refresh admin list
        fetchAdmins();
      } else {
        alert(response.detail || response.message || "Failed to create admin");
      }
    } catch (error: any) {
      console.error("Error creating admin:", error);
      alert(error.response?.data?.detail || "Failed to create admin");
    } finally {
      setCreating(false);
    }
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Never";
    return new Date(dateString).toLocaleString();
  };

  if (role !== "SUPER_ADMIN") {
    return null;
  }

  return (
    <div className="min-h-screen bg-transparent text-white font-sans p-6">
      <div className="max-w-7xl mx-auto">
        <h1 className="text-3xl font-bold text-yellow-300 tracking-wide mb-6">
          👥 Admin Management
        </h1>

        {/* Create Admin Form */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 shadow-md rounded-xl p-6 mb-8">
          <h2 className="text-xl font-semibold text-yellow-300 mb-4">
            Create New Admin
          </h2>

          <form
            onSubmit={handleCreateAdmin}
            className="grid grid-cols-1 md:grid-cols-2 gap-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username *
              </label>
              <input
                type="text"
                name="username"
                value={formData.username}
                onChange={handleInputChange}
                required
                minLength={3}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                placeholder="admin_username"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email *
              </label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password *
              </label>
              <input
                type="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                required
                minLength={6}
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                placeholder="Min 6 characters"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Phone (Optional)
              </label>
              <input
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                pattern="[0-9]{10}"
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                placeholder="10 digit number"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Role *
              </label>
              <select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                required
                className="w-full px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-300"
              >
                {CREATE_ADMIN_ROLES.map((role) => (
                  <option key={role} value={role} className="bg-gray-800">
                    {role}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex items-end">
              <button
                type="submit"
                disabled={creating}
                className="w-full bg-yellow-300 text-black font-semibold px-6 py-2 rounded-lg hover:bg-yellow-400 transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {creating ? "Creating..." : "➕ Create Admin"}
              </button>
            </div>
          </form>
        </div>

        {/* Admin List Table */}
        <div className="bg-white/10 backdrop-blur-md border border-white/20 shadow-md rounded-xl p-6">
          <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4 mb-4">
            <h2 className="text-xl font-semibold text-yellow-300">
              All Admins ({adminCount}{adminTotal !== adminCount ? ` of ${adminTotal}` : ""})
            </h2>
            <button
              onClick={() => fetchAdmins()}
              disabled={loading}
              className="bg-yellow-300 text-black font-semibold px-4 py-2 rounded-lg hover:bg-yellow-400 transition disabled:opacity-50"
            >
              {loading ? "Loading..." : "🔄 Refresh"}
            </button>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-xl p-5 mb-6">
            <h3 className="text-lg font-semibold text-yellow-300 mb-4">
              Filter Admins
            </h3>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Search
                </label>
                <input
                  type="text"
                  name="search"
                  value={listFilters.search}
                  onChange={handleListFilterChange}
                  placeholder="Search by ID, username, email, phone or role"
                  className="w-full px-4 py-2 bg-slate-900/60 border border-slate-700/60 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-yellow-300"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Role
                </label>
                <select
                  name="role"
                  value={listFilters.role}
                  onChange={handleListFilterChange}
                  className="w-full px-4 py-2 bg-slate-900/60 border border-slate-700/60 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-300"
                >
                  <option value="" className="text-white bg-slate-900/80">
                    All Roles
                  </option>
                  {AVAILABLE_ROLES.map((roleOption) => (
                    <option key={roleOption} value={roleOption} className="text-white bg-slate-900/80">
                      {roleOption}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Status
                </label>
                <select
                  name="status"
                  value={listFilters.status}
                  onChange={handleListFilterChange}
                  className="w-full px-4 py-2 bg-slate-900/60 border border-slate-700/60 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-yellow-300"
                >
                  <option value="" className="text-white bg-slate-900/80">
                    All Status
                  </option>
                  <option value="active" className="text-white bg-slate-900/80">
                    Active
                  </option>
                  <option value="inactive" className="text-white bg-slate-900/80">
                    Inactive
                  </option>
                </select>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-4">
              <button
                type="button"
                onClick={applyListFilters}
                disabled={loading}
                className="bg-yellow-300 text-black font-semibold px-5 py-2 rounded-lg hover:bg-yellow-400 transition disabled:opacity-50"
              >
                Apply Filters
              </button>
              <button
                type="button"
                onClick={clearListFilters}
                disabled={loading}
                className="bg-transparent text-yellow-300 border border-yellow-300 font-semibold px-5 py-2 rounded-lg hover:bg-yellow-300/10 transition disabled:opacity-50"
              >
                Clear Filters
              </button>
            </div>
          </div>

          {loading ? (
            <div className="text-center py-8 text-gray-400">
              Loading admins...
            </div>
          ) : admins.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              No admins found
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/20">
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Admin ID
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Username
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Email
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Phone
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Role
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Status
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Created
                    </th>
                    <th className="text-left py-3 px-4 text-gray-300 font-medium">
                      Last Login
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {admins.map((admin) => (
                    <tr
                      key={admin.admin_id}
                      className="border-b border-white/10 hover:bg-white/5"
                    >
                      <td className="py-3 px-4 text-sm">{admin.admin_id}</td>
                      <td className="py-3 px-4 text-sm font-medium">
                        {admin.username}
                      </td>
                      <td className="py-3 px-4 text-sm">{admin.email}</td>
                      <td className="py-3 px-4 text-sm">
                        {admin.phone || "N/A"}
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                            admin.role === "SUPER_ADMIN"
                              ? "bg-purple-500/20 text-purple-300"
                              : "bg-blue-500/20 text-blue-300"
                          }`}
                        >
                          {admin.role}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span
                          className={`inline-block px-2 py-1 text-xs font-semibold rounded ${
                            admin.is_active
                              ? "bg-green-500/20 text-green-300"
                              : "bg-red-500/20 text-red-300"
                          }`}
                        >
                          {admin.is_active ? "Active" : "Inactive"}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {formatDate(admin.created_at)}
                      </td>
                      <td className="py-3 px-4 text-sm">
                        {formatDate(admin.last_login_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
